import React, { useState, useEffect } from 'react';
import { Brain, Clock, CheckCircle2, Trash2, BrainCircuit, Edit2, X, Save } from 'lucide-react';
import { format } from 'date-fns';

interface MemoryRecord {
  id: number;
  type: string;
  category: string;
  subCategory: string | null;
  amount: number;
  originalAmount: number;
  description: string;
  dueDate: string | null;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
}

export default function LunaMemoryView({ storeApiKey }: { storeApiKey: string }) {
  const [records, setRecords] = useState<MemoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState<MemoryRecord | null>(null);

  const fetchMemories = async () => {
    try {
      const res = await fetch('/api/pos/luna-memory', {
        headers: { 'x-store-api-key': storeApiKey }
      });
      const data = await res.json();
      if (Array.isArray(data)) setRecords(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemories();
  }, [storeApiKey]);

  const deleteRecord = async (id: number) => {
    if (!confirm('Are you sure you want to delete this?')) return;
    try {
      await fetch(`/api/pos/luna-memory/${id}`, {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey }
      });
      setRecords(r => r.filter(x => x.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const markCompleted = async (id: number) => {
    try {
      await fetch(`/api/pos/luna-memory/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({ status: 'COMPLETED' })
      });
      setRecords(r => r.map(x => x.id === id ? { ...x, status: 'COMPLETED' } : x));
    } catch (e) {
      console.error(e);
    }
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRecord) return;
    try {
      const res = await fetch(`/api/pos/luna-memory/${editingRecord.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({
          description: editingRecord.description,
          amount: editingRecord.amount,
          category: editingRecord.category,
          dueDate: editingRecord.dueDate,
          status: editingRecord.status,
        })
      });
      if (res.ok) {
        setRecords(r => r.map(x => x.id === editingRecord.id ? editingRecord : x));
        setEditingRecord(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatRs = (amt: number) => 'Rs. ' + amt.toLocaleString('en-LK', { minimumFractionDigits: 2 });

  // Dynamically group all records by Luna's type instead of category (user preference)
  const groupedRecords = records.reduce((acc, r) => {
    const groupKey = r.type || 'General';
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(r);
    return acc;
  }, {} as Record<string, MemoryRecord[]>);

  // Sort categories so they look nice
  const categories = Object.keys(groupedRecords).sort();

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-[0_0_20px_rgba(139,92,246,0.3)]">
          <BrainCircuit size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Luna Memory</h1>
          <p className="text-sm text-slate-500 font-mono">Dynamic AI Categorization & Tracking</p>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 font-mono animate-pulse">Analyzing memories...</div>
      ) : records.length === 0 ? (
        <div className="text-center py-20 text-slate-400 font-mono italic">
          Luna has no memories yet. Tell her to remember something!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => (
            <div key={category} className="col-span-1 space-y-4">
              <h2 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 px-1">
                <Brain size={14} className="text-violet-500" /> {category}
              </h2>
              <div className="space-y-3">
                {groupedRecords[category].map(r => (
                  <div key={r.id} className="p-4 rounded-2xl bg-white border border-slate-100 shadow-sm relative overflow-hidden group hover:border-violet-200 transition-colors">
                    {/* Status accent line for tasks/reminders */}
                    {r.dueDate && (
                      <div className={`absolute top-0 left-0 w-1 h-full ${r.status === 'COMPLETED' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    )}
                    
                    <div className="flex justify-between items-start pl-2">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-700 leading-snug">{r.description}</p>
                        
                        {r.dueDate && (
                          <p className="text-xs text-amber-600 mt-1.5 font-mono font-medium flex items-center gap-1">
                            <Clock size={11} /> {format(new Date(r.dueDate), 'MMM dd, h:mm a')}
                          </p>
                        )}
                        
                        {r.amount > 0 && (
                          <p className="text-xs font-black text-rose-500 mt-1.5">
                            {r.type === 'INCOME' || r.type === 'Extra Incomes' ? '+' : ''}{formatRs(r.amount)}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditingRecord(r)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors">
                          <Edit2 size={14} />
                        </button>
                        {r.dueDate && r.status === 'PENDING' && (
                          <button onClick={() => markCompleted(r.id)} className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 transition-colors">
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button onClick={() => deleteRecord(r.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      {editingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-slate-200">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <BrainCircuit size={18} className="text-violet-500" />
                Edit Luna Memory
              </h3>
              <button onClick={() => setEditingRecord(null)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Description</label>
                <input 
                  type="text" 
                  value={editingRecord.description}
                  onChange={e => setEditingRecord({...editingRecord, description: e.target.value})}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium text-slate-700"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Category</label>
                  <input 
                    type="text" 
                    value={editingRecord.category}
                    onChange={e => setEditingRecord({...editingRecord, category: e.target.value})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium text-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Amount</label>
                  <input 
                    type="number" 
                    value={editingRecord.amount}
                    onChange={e => setEditingRecord({...editingRecord, amount: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium text-slate-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Due Date</label>
                  <input 
                    type="datetime-local" 
                    value={editingRecord.dueDate ? new Date(editingRecord.dueDate).toISOString().slice(0, 16) : ''}
                    onChange={e => setEditingRecord({...editingRecord, dueDate: e.target.value ? new Date(e.target.value).toISOString() : null})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium text-slate-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Status</label>
                  <select 
                    value={editingRecord.status}
                    onChange={e => setEditingRecord({...editingRecord, status: e.target.value as any})}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all font-medium text-slate-700"
                  >
                    <option value="PENDING">PENDING</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                </div>
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setEditingRecord(null)} className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-violet-500 hover:bg-violet-600 shadow-lg shadow-violet-500/30 transition-all flex justify-center items-center gap-2">
                  <Save size={18} /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
